// src/pages/DossierProjectPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Separator from "@radix-ui/react-separator";
import { dossierService } from "../lib/dossierService";
import { buildDossierPath, DOSSIER_MODES, isExpressDossierMode } from "../data/dossierModes";
import DossierEmptyState from "../components/dossier/DossierEmptyState";

const EMPTY_PROJECTS = Object.freeze({});

/* ------------------------ Utils de mapeo de documentos ------------------------ */
const removeDiacritics = (s = "") =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const norm = (s = "") =>
  removeDiacritics(String(s).toLowerCase()).replace(/[^a-z0-9]+/g, " ").trim();

/** Escoge el “mejor” doc por prioridad: docx > pdf > resto */
const isDocx = (d) => d?.ext?.toLowerCase() === "docx" || /\.docx($|\?)/i.test(d?.url || "");
const isPdf  = (d) => d?.ext?.toLowerCase() === "pdf"  || /\.pdf($|\?)/i.test(d?.url || "");
const better = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  // Prioridad: docx > pdf > otro
  const ra = isDocx(a) ? 2 : isPdf(a) ? 1 : 0;
  const rb = isDocx(b) ? 2 : isPdf(b) ? 1 : 0;
  return rb > ra ? b : a;
};

/** Dado un array de docs, genera un mapa { phaseKey: bestDoc } */
const buildPhaseDocMap = (docs = [], phaseKeys = []) => {
  const map = {};

  // 1) Mapeo directo por campos comunes
  docs.forEach((d) => {
    const key =
      d.phaseKey || d.projectPhase || d.projectKey || d.phase || d.slug || null;
    if (key) map[key] = better(map[key], d);
  });

  // 2) Fallback por coincidencia en nombre/url con el phaseKey
  if (phaseKeys?.length) {
    const normalizedDocs = docs.map((d) => ({
      ref: d,
      nName: norm(d.name || ""),
      nUrl: norm(d.url || ""),
    }));

    phaseKeys.forEach((k) => {
      if (map[k]) return; // ya encontrado
      const nk = norm(k);
      // Busca docs cuyo nombre o url contenga el key normalizado
      const candidates = normalizedDocs
        .filter((x) => x.nName.includes(nk) || x.nUrl.includes(nk))
        .map((x) => x.ref);
      if (candidates.length) {
        map[k] = candidates.reduce(better, null);
      }
    });
  }

  return map;
};

/* ------------------------------ Subcomponentes ------------------------------ */

function SectionList({ title, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="mt-3">
      {title && <h4 className="font-semibold mb-2">{title}</h4>}
      <ul className="list-disc list-inside space-y-1 text-sm leading-relaxed">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}

function PhaseCard({ ns, phaseKey, doc, mode }) {
  const { t } = useTranslation();
  const guia = t(`${ns}.guia`, { defaultValue: "" });
  const title = t(`${ns}.titol_apartat`, { defaultValue: "" });
  const desc = t(`${ns}.descripcio`, { defaultValue: "" });

  const continguts = t(`${ns}.blocs_i_competencies.continguts`, {
    returnObjects: true,
    defaultValue: [],
  });
  const competencies = t(`${ns}.blocs_i_competencies.competencies`, {
    returnObjects: true,
    defaultValue: [],
  });

  if (!guia && !title && !desc) return null;

  return (
    <section className="bg-white/80 backdrop-blur rounded-2xl border p-6 space-y-3">
      <h3 className="text-xl font-semibold">{guia || title}</h3>
      {title && guia && <p className="text-sm text-gray-600">{title}</p>}
      {desc && (
        <p className="text-sm whitespace-pre-line leading-relaxed">{desc}</p>
      )}

      {(continguts?.length || competencies?.length) && (
        <div className="grid md:grid-cols-2 gap-4">
          <SectionList title={null} items={continguts} />
          <SectionList title={null} items={competencies} />
        </div>
      )}

      <div className="pt-1 flex gap-2 flex-wrap">
        <Link
          to={buildDossierPath(`/dossier/projecte/${phaseKey}`, mode)}
          className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 border rounded-xl bg-white hover:bg-gray-50 transition"
        >
          {t("ui.common.viewDetails", "Veure detall")}
        </Link>

        {doc?.url ? (
          <a
            href={doc.url}
            target="_blank"
            rel="noreferrer"
            download
            className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 border rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition"
            title={doc.name || t("dossier_didactic.download", "Descarregar")}
          >
            {t("dossier_didactic.download", "Descarregar")}
          </a>
        ) : null}
      </div>
    </section>
  );
}

/* --------------------------------- Página --------------------------------- */

export default function DossierProjectPage({ mode = DOSSIER_MODES.STANDARD }) {
  const { t } = useTranslation();
  const projectNs = "dossier_didactic.projectes";
  const isExpress = isExpressDossierMode(mode);
  const projectsObj = isExpress
    ? EMPTY_PROJECTS
    : t(projectNs, { returnObjects: true, defaultValue: {} });

  // Mantén el orden natural si lo necesitas; aquí usamos el orden de las claves
  const phaseKeys = useMemo(() => Object.keys(projectsObj || {}), [projectsObj]);

  // Mapa { phaseKey: {url, name, ext, ...} }
  const [byPhaseDoc, setByPhaseDoc] = useState({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const docs = await dossierService.listDocuments?.(mode);
        // Filtramos por documentos que presumiblemente pertenezcan a proyecto (sin sessionId)
        const projectDocs = (docs || []).filter((d) => !d.sessionId);
        const map = buildPhaseDocMap(projectDocs, phaseKeys);
        if (mounted) setByPhaseDoc(map);
      } catch (e) {
        if (mounted) setByPhaseDoc({});
      }
    })();
    return () => {
      mounted = false;
    };
  }, [mode, phaseKeys]);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold">
          {t(
            "ui.dossier.projecte.title",
            t("dossier_didactic.projectes.title", "Projecte Idees Genials")
          )}
        </h2>
        <p className="text-sm text-gray-600">
          {t("ui.dossier.projecte.subtitle", "Fases")}
        </p>
      </div>

      <Separator.Root className="bg-gray-200 h-px w-full" />

      {isExpress ? (
        <DossierEmptyState />
      ) : (
      <div className="space-y-6">
        {phaseKeys.map((k) => (
          <PhaseCard
            key={k}
            ns={`${projectNs}.${k}`}
            phaseKey={k}
            doc={byPhaseDoc[k] || null}
            mode={mode}
          />
        ))}
      </div>
      )}
    </div>
  );
}
